// ─── Conceptual Figure Engine ──────────────────────────────────────────────────
// PIPELINE A: Generates mermaid diagrams for conceptual chapters (Ch1-3 only).
//
// Ch1 — Introduction: governance ecosystem, research context map
// Ch2 — Literature Review: theoretical framework, conceptual model
// Ch3 — Methodology: research design workflow, sampling architecture
//
// Figures are injected as data-thesis-figure mermaid tokens, rendered by
// ThesisFigureExtension inside TipTap. Fully self-contained — no DB lookup.

import { buildConceptualFigureHTML, wrapFigureToken, injectFigureAfterParagraph } from './inlineFigureRenderer'

// ── Chapter profiles ──────────────────────────────────────────────────────────

const PROFILES = {
  1: {
    cues: [/governance/i, /institutional/i, /stakeholder/i, /policy/i, /context/i, /background/i, /overview/i, /study\s+area/i],
    figures: ['ecosystem', 'context_map'],
  },
  2: {
    cues: [/framework/i, /theoretical/i, /model/i, /comparative/i, /synthesis/i, /relationship/i, /proposition/i, /hypothesis/i],
    figures: ['theoretical_framework', 'conceptual_model'],
  },
  3: {
    cues: [/research\s+design/i, /sampling/i, /mixed.?method/i, /questionnaire/i, /data\s+collection/i, /methodology/i, /approach/i],
    figures: ['research_design', 'sampling_flow'],
  },
}

// ── Mermaid diagram generators ────────────────────────────────────────────────

function ecosystemDiagram() {
  return `graph TD
    A[Research Context] --> B[Institutional Framework]
    A --> C[Policy Environment]
    B --> D[Governance Structures]
    B --> E[Administrative Capacity]
    C --> F[Regulatory Mechanisms]
    C --> G[Stakeholder Interests]
    D --> H[Study Focus Area]
    E --> H
    F --> H
    G --> H
    style A fill:#1e3a5f,color:#fff,stroke:#1e3a5f
    style H fill:#059669,color:#fff,stroke:#059669`
}

function contextMapDiagram() {
  return `graph LR
    A[Research Problem] --> B[Theoretical Gap]
    B --> C[Study Objectives]
    C --> D[Research Questions]
    D --> E[Hypotheses]
    A --> F[Practical Significance]
    F --> G[Policy Implications]
    style A fill:#1e3a5f,color:#fff
    style E fill:#2563eb,color:#fff
    style G fill:#059669,color:#fff`
}

function theoreticalFrameworkDiagram() {
  return `graph TD
    A[Theoretical Foundation] --> B[Institutional Theory]
    A --> C[Behavioural Theory]
    A --> D[Contextual Factors]
    B --> E[Key Constructs]
    C --> E
    D --> E
    E --> F[Conceptual Model]
    F --> G[Testable Propositions]
    style A fill:#7c3aed,color:#fff
    style F fill:#1e3a5f,color:#fff
    style G fill:#059669,color:#fff`
}

function conceptualModelDiagram(thesis) {
  const n = thesis?.sample_size || 120
  return `graph LR
    IV["Independent Variables"] --> DV["Dependent Variable"]
    MED["Mediating Variable"] --> DV
    IV --> MED
    MOD["Moderating Variable"] -.->|moderates| IV
    style IV fill:#1e3a5f,color:#fff
    style DV fill:#059669,color:#fff
    style MED fill:#2563eb,color:#fff
    style MOD fill:#7c3aed,color:#fff`
}

function researchDesignDiagram() {
  return `flowchart TD
    A[Research Problem] --> B[Literature Review]
    B --> C[Conceptual Framework]
    C --> D{Research Approach}
    D --> E[Quantitative Phase]
    D --> F[Qualitative Phase]
    E --> G[Survey Instrument]
    F --> H[Interview Protocol]
    G --> I[Data Collection]
    H --> I
    I --> J[Statistical Analysis]
    J --> K[Findings and Discussion]
    K --> L[Conclusions and Recommendations]
    style A fill:#1e3a5f,color:#fff
    style L fill:#059669,color:#fff`
}

function samplingFlowDiagram(thesis) {
  const n   = thesis?.sample_size || 120
  const nQ  = Math.round(n * 0.83)
  const nI  = Math.round(n * 0.08)
  return `flowchart LR
    A[Target Population] --> B[Sampling Frame]
    B --> C{Sampling Method}
    C --> D[Stratified Random]
    C --> E[Purposive]
    D --> F["Survey Sample n=${nQ}"]
    E --> G["Interview Sample n=${nI}"]
    F --> H["Final Sample N=${n}"]
    G --> H
    style A fill:#1e3a5f,color:#fff
    style H fill:#059669,color:#fff`
}

const GENERATORS = {
  ecosystem:             ecosystemDiagram,
  context_map:           contextMapDiagram,
  theoretical_framework: theoreticalFrameworkDiagram,
  conceptual_model:      conceptualModelDiagram,
  research_design:       researchDesignDiagram,
  sampling_flow:         samplingFlowDiagram,
}

const TITLES = {
  ecosystem:             'Governance Ecosystem and Institutional Framework',
  context_map:           'Research Context and Study Rationale',
  theoretical_framework: 'Theoretical Framework and Conceptual Foundations',
  conceptual_model:      'Proposed Conceptual Model of Key Relationships',
  research_design:       'Research Design and Methodological Workflow',
  sampling_flow:         'Sampling Architecture and Data Collection Process',
}

const CAPTIONS = {
  ecosystem:             'Schematic representation of the governance ecosystem and key institutional actors within the study context.',
  context_map:           'Mapping of the research problem, theoretical gap, study objectives, and policy implications.',
  theoretical_framework: 'Synthesis of theoretical perspectives informing the conceptual model of the study.',
  conceptual_model:      'Proposed conceptual model illustrating hypothesised relationships among the primary study variables.',
  research_design:       'Sequential workflow illustrating the mixed-methods research design adopted in this study.',
  sampling_flow:         'Sampling architecture showing the stratification strategy and final sample composition.',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ')
}

function alreadyHasFigure(html, figureId) {
  return html.includes(`data-figure-id="${figureId}"`)
}

// ── Main engine ───────────────────────────────────────────────────────────────

/**
 * Generate and inject conceptual figures for a single chapter.
 * Only operates on Ch1, Ch2, Ch3 (chapter_number ≤ 3).
 *
 * @param {{ id, title, content, chapter_number }} chapter
 * @param {{ thesis }} context
 * @returns {{ html: string, injected: Array, skipped: string[] }}
 */
export function generateConceptualFigures(chapter, context = {}) {
  const { thesis = {} } = context
  const chNum   = chapter.chapter_number || 1
  const profile = PROFILES[chNum]

  if (!profile) {
    return { html: chapter.content, injected: [], skipped: [`Ch${chNum}: no conceptual profile defined (conceptual figures are for Ch1-3 only)`] }
  }

  const plain = stripHtml(chapter.content)
  if (!profile.cues.some(re => re.test(plain))) {
    return { html: chapter.content, injected: [], skipped: [`Ch${chNum}: no conceptual keyword cues detected in content`] }
  }

  let html     = chapter.content
  const injected = []
  const skipped  = []
  let figNum   = countExistingFigures(html, chNum) + 1

  for (const figKey of profile.figures) {
    const figureId = `cpt_${chNum}_${figKey}`

    if (alreadyHasFigure(html, figureId)) {
      skipped.push(`${figKey}: already present in chapter`)
      continue
    }

    const generator = GENERATORS[figKey]
    if (!generator) { skipped.push(`${figKey}: no generator`); continue }

    const mermaidCode = generator(thesis)
    const title       = TITLES[figKey]
    const caption     = CAPTIONS[figKey]

    const tokenHTML = buildConceptualFigureHTML({
      figureId, mermaidCode, title, caption,
      chapterNumber: chNum,
      figureNumber:  figNum,
    })

    html = injectFigureAfterParagraph(html, wrapFigureToken(tokenHTML), profile.cues)

    injected.push({
      figureId,
      chapterTitle:  chapter.title,
      chapterNumber: chNum,
      figureNumber:  figNum,
      title,
      figureType:    figKey,
    })
    figNum++
  }

  return { html, injected, skipped }
}

function countExistingFigures(html, chNum) {
  const re = new RegExp(`data-figure-number="${chNum}\\.\\d+"`, 'g')
  return ((html || '').match(re) || []).length
}
